import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import {
  useSolarManagementSystemDetailQuery,
  useSolarManagementSystemDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import { PanelFrame, ViewMode } from '../shared-ui/panel-frame';
import moment from 'moment';
import { DeviceInformation } from '../shared-ui/device-info';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';

interface SolarManagementSystemProps {
  id: string;
}

export const SolarManagementSystemComponent: React.FC<
  SolarManagementSystemProps
> = ({ id }) => {
  const [device, setDevice] = React.useState<any>();
  const { data, loading, error } = useSolarManagementSystemDetailQuery({
    variables: { id },
  });

  useSolarManagementSystemDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  React.useEffect(() => {
    if (data?.solarManagementSystemById) {
      setDevice(data.solarManagementSystemById);
    }
  }, [data]);

  if (loading) return <DeviceLoadingSkeleton size="large" />;
  if (error)
    return <Typography color="error">Error loading device data</Typography>;
  if (!device) return null;

  const deviceInfo: DeviceInformation[] = [
    {
      'System Name': device.name || 'Unnamed Solar Management System',
      Location: device.location || 'Unknown',
      'Last Updated': moment(device.timestamp).fromNow(),
      'Solar Irradiance': formatDeviceMetric({
        value: device.solarIrradiance?.value ?? undefined,
        units: device.solarIrradiance?.units ?? undefined,
      }),
      'Panel Temperature': formatDeviceMetric({
        value: device.panelTemperature?.value ?? undefined,
        units: device.panelTemperature?.units ?? undefined,
      }),
      'Degradation Rate': formatDeviceMetric({
        value: device.degradationRate?.value ?? undefined,
        units: device.degradationRate?.units ?? undefined,
      }),
      'Shading Factor': formatDeviceMetric({
        value: device.shadingFactor?.value ?? undefined,
        units: device.shadingFactor?.units ?? undefined,
      }),
      'Tilt Angle': formatDeviceMetric({
        value: device.tiltAngle?.value ?? undefined,
        units: device.tiltAngle?.units ?? undefined,
      }),
      Azimuth: formatDeviceMetric({
        value: device.azimuth?.value ?? undefined,
        units: device.azimuth?.units ?? undefined,
      }),
      'Tracker System': device.isTrackerSystem?.value ? 'Yes' : 'No',
      Property: device.property?.title || 'Not assigned',
      Panel: device.panel?.displayName || 'Not assigned',
      Source: device.source?.name || 'Not assigned',
    },
  ];

  return (
    <PanelFrame
      title="Solar Management System"
      iconType="material-symbols:solar-power"
      panel={device}
      infoEntries={deviceInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              System Metrics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Solar Irradiance
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.solarIrradiance?.value ?? undefined,
                    units: device.solarIrradiance?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Panel Temperature
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.panelTemperature?.value ?? undefined,
                    units: device.panelTemperature?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Degradation Rate
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.degradationRate?.value ?? undefined,
                    units: device.degradationRate?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Shading Factor
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.shadingFactor?.value ?? undefined,
                    units: device.shadingFactor?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Panel Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Tilt Angle
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.tiltAngle?.value ?? undefined,
                    units: device.tiltAngle?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Azimuth
                </Typography>
                <Typography variant="body1">
                  {formatDeviceMetric({
                    value: device.azimuth?.value ?? undefined,
                    units: device.azimuth?.units ?? undefined,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle2" color="textSecondary">
                  Tracker System
                </Typography>
                <Typography variant="body1">
                  {device.isTrackerSystem?.value ? 'Yes' : 'No'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </PanelFrame>
  );
};
